import Viewport from 'pixi-viewport';
import { getGameSize, IGameSizeInfo, NODES_PER_MAP } from '../Config/Levels';
import { app } from '../index';
import SoundManager from '../Sound/SoundManager';
import { IMapData } from '../State/reducers/player';
import AreaMap from '../UI/Map/AreaMap';
import TopMapUi from '../UI/Map/TopMapUi';
import { waitForMs } from '../Utils/AsyncUtils';
import { AddBoosterAnimation } from '../UI/Animations/UIAnimations';
import { createPixiText } from '../Utils/Utils';
import Core, { GameOrientation } from '../Core/Core';
import GameLoader from '../Core/Assets';
import { IMapChests, MapChestsStates } from '../Config/MapChests';
import { enableWheelScroll } from '../Utils/Platform';

export default class MapView extends PIXI.Container {
    public ui: TopMapUi;
    public areaMap: AreaMap;
    private viewport: Viewport;
    private rewardAnims: any[] = [];
    private playerNode: number;
    private topPosition: number;

    // private dirtyYPos: number;

    constructor(
        mapId: string,
        levels: IMapData,
        mapChests: IMapChests[],
        unlockedChests: MapChestsStates[]
    ) {
        super();

        this.areaMap = new AreaMap(mapId, levels);

        this.viewport = this.createViewPort();

        app.stage.addChildAt(this.viewport, 0);

        this.viewport.drag({ wheel: enableWheelScroll() }).decelerate();

        this.viewport.addChild(this.areaMap);

        this.viewport.on('drag-start', () => {
            this.areaMap.dragging = true;
        });

        this.viewport.on('drag-end', () => {
            this.areaMap.dragging = false;
        });

        if (Core.orientation === GameOrientation.LANDSCAPE) {
            this.viewport.fitWidth();
        }

        this.addChests(mapChests, unlockedChests);

        this.startMapMusic();
    }

    public update() {
        if (this.areaMap) {
            this.areaMap.update();
        }
    }

    public cleanUp() {
        SoundManager.stopSound('map-music', true, 1000);
        this.areaMap.cleanUp();
        app.stage.removeChild(this.viewport);
        // this.viewport.removeListeners();
        this.viewport.destroy();
        this.viewport = null;
    }

    // @ts-ignore
    public resize(gameSize: IGameSizeInfo) {
        if (this.viewport) {
            const scale = gameSize.gameScale;
            const w = gameSize.gameWidth * scale;
            const h = gameSize.gameHeight * scale;
            this.viewport.resize(w, h);
        }

        this.centerOnPlayer(this.playerNode);
    }

    public centerOnPlayer(nodeIndex: number) {
        const areaMap = this.areaMap;
        const nodesLength = areaMap.nodes.length;

        if (nodeIndex > nodesLength) {
            nodeIndex = nodesLength;
        }
        this.playerNode = nodeIndex;

        const currentNode = areaMap.nodes[nodeIndex - 1] || areaMap.nodes[0];
        const scrollDuration = /* first ? 4000 :*/ 1;
        const viewport = this.viewport;
        const isLandscape = Core.orientation === GameOrientation.LANDSCAPE;
        const zoomAmt: number = isLandscape ? 1 : 1.5;
        const snapOffset: number = isLandscape ? 1 : 1.85;
        if (!viewport) return;
        viewport.clamp({ direction: 'all' });
        if (isLandscape) {
            viewport.fitWidth();
        } else {
            viewport.snapZoom({ time: scrollDuration, width: areaMap.width / zoomAmt });
        }

        viewport.snap(areaMap.width / snapOffset, currentNode.y, {
            time: scrollDuration,
            removeOnComplete: true,
            interrupt: true,
        });
        viewport.once('moved-end', () => {
            viewport.clamp({
                left: viewport.left,
                right: viewport.right,
                top: areaMap.nodes.length === NODES_PER_MAP ? true : this.topPosition || true,
                bottom: true,
            });
        });
    }

    // show node rewards
    public async showNodeRewards(nodeIndex: number, boosterChange: string[]) {
        await waitForMs(600);
        const boosterAnimation = new AddBoosterAnimation(
            boosterChange,
            this,
            nodeIndex > 49 ? nodeIndex - 1 : nodeIndex
        );
        this.rewardAnims.push(boosterAnimation);
        await boosterAnimation
            .showBoostersAdded(this)
            .then(() => {
                this.rewardAnims.splice(this.rewardAnims.indexOf(boosterAnimation), 1);
            })
            .then(() => {
                this.rewardAnims.splice(this.rewardAnims.indexOf(boosterAnimation), 1);
            });

        return;
    }

    public addComingSoon() {
        const areaMap = this.areaMap;
        const isLandscape = Core.orientation === GameOrientation.LANDSCAPE;
        const snapOffset: number = isLandscape ? 1 : 1.85;
        const banner = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/coming-soon-banner']);
        banner.anchor.set(0.5);
        const text = createPixiText(
            Core.local.getText('comingsoon'),
            new PIXI.Rectangle(0, 0, banner.width, banner.height),
            'Baloo',
            30,
            28,
            0xffffff
        );
        banner.scale.set(2);
        banner.addChild(text);
        banner.x = isLandscape ? areaMap.width * 0.5 : areaMap.width / snapOffset;
        banner.y = areaMap.nodes[areaMap.nodes.length - 1].y - banner.height * 4;

        areaMap.addChild(banner);
        this.topPosition = banner.y - banner.height * 7;
    }

    private createViewPort() {
        const viewport = new Viewport({
            screenWidth: getGameSize().gameWidth * getGameSize().gameScale,
            screenHeight: getGameSize().gameHeight * getGameSize().gameScale,
            worldWidth: this.areaMap.width,
            worldHeight: this.areaMap.height,
            interaction: app.renderer.plugins.interaction, // the interaction module is important for wheel() to work properly when renderer.view is placed or scaled
        });

        return viewport;
    }

    private async startMapMusic() {
        await GameLoader.loadAssetList({
            assetList: [Core.store.getState().StatePlayer.mapId + '-map'],
        });
        SoundManager.playMusic('map-music', true, 1000);
    }

    private async addChests(mapChests: IMapChests[], unlockedChests: MapChestsStates[]) {
        // map chests need tutorial assets for speech bubble
        await GameLoader.loadAssetList({ assetList: ['mapui', 'tutorial'] });

        mapChests.forEach(element => {
            this.areaMap.addChests(element, unlockedChests[element.index]);
        });
    }
}
